function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function normalizeMethod(method = 'GET') {
  return String(method).toUpperCase()
}

function resolveRequestUrl(baseURL = '', url = '') {
  if (!url) return baseURL || ''
  if (/^https?:\/\//i.test(url)) return url
  if (!baseURL) return url

  const normalizedBaseURL = String(baseURL).replace(/\/$/, '')
  const normalizedUrl = String(url).startsWith('/') ? String(url) : `/${url}`

  return `${normalizedBaseURL}${normalizedUrl}`
}

function shouldMaskKey(key = '') {
  return /(authorization|token|password|passwd|pwd|pin|secret|sign|aeskey|aesiv|publicpem|privatekey)/i.test(key)
}

function shouldTrimKey(key = '') {
  return /(front_page_img|back_page_img|image|img|base64|photo|portrait|body)/i.test(key)
}

function formatTrimmedValue(value) {
  const normalizedValue = String(value)
  return `[已裁切 length=${normalizedValue.length} prefix=${normalizedValue.slice(
    0,
    32
  )} suffix=${normalizedValue.slice(-16)}]`
}

function sanitizePayload(payload, currentKey = '') {
  if (shouldMaskKey(currentKey)) {
    return '[已隐藏]'
  }

  if (payload == null) {
    return payload
  }

  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    const formDataResult = {}
    payload.forEach((value, key) => {
      formDataResult[key] = sanitizePayload(value, key)
    })
    return formDataResult
  }

  if (typeof File !== 'undefined' && payload instanceof File) {
    return `[File ${payload.name || 'unknown'}]`
  }

  if (typeof Blob !== 'undefined' && payload instanceof Blob) {
    return `[Blob ${payload.type || 'unknown'} ${payload.size} bytes]`
  }

  if (Array.isArray(payload)) {
    return payload.map(item => sanitizePayload(item, currentKey))
  }

  if (isPlainObject(payload)) {
    return Object.fromEntries(Object.entries(payload).map(([key, value]) => [key, sanitizePayload(value, key)]))
  }

  if (typeof payload === 'string') {
    if (shouldTrimKey(currentKey) || payload.length > 512) {
      return formatTrimmedValue(payload)
    }
  }

  return payload
}

function getApiLogMeta(config = {}) {
  if (config.__apiLogMeta) {
    return config.__apiLogMeta
  }

  return {
    method: normalizeMethod(config.method),
    requestUrl: resolveRequestUrl(config.baseURL, config.url),
    startedAt: Date.now(),
    payload: undefined
  }
}

function getDuration(startedAt) {
  if (!startedAt) {
    return undefined
  }

  return `${Date.now() - startedAt}ms`
}

function normalizeTraceId(traceId) {
  if (traceId == null || traceId === '') {
    return undefined
  }

  if (Array.isArray(traceId)) {
    return traceId.join(',')
  }

  return String(traceId)
}

function resolveResponseTraceId(response) {
  const headers = response && response.headers ? response.headers : {}
  return normalizeTraceId(headers['trace-id'])
}

function safeStringify(value) {
  if (value === undefined) {
    return ''
  }

  try {
    return JSON.stringify(value)
  } catch (error) {
    return JSON.stringify({
      message: '日志序列化失败',
      error: error && error.message ? error.message : String(error)
    })
  }
}

function formatLogLine(prefix, meta, details) {
  const detailString = safeStringify(details)
  return detailString
    ? `${prefix} ${meta.method} ${meta.requestUrl} ${detailString}`
    : `${prefix} ${meta.method} ${meta.requestUrl}`
}

export function attachApiRequestMeta(config, payload) {
  const meta = {
    method: normalizeMethod(config.method),
    requestUrl: resolveRequestUrl(config.baseURL, config.url),
    startedAt: Date.now(),
    payload: sanitizePayload(payload)
  }

  config.__apiLogMeta = meta
  return meta
}

export function logApiRequest(meta) {
  if (!meta) {
    return
  }

  console.log(
    formatLogLine('[API 请求]', meta, {
      method: meta.method,
      url: meta.requestUrl,
      payload: meta.payload
    })
  )
}

export function logApiResponse(response, res, level = 'log') {
  const meta = getApiLogMeta(response && response.config)
  const logger = typeof console[level] === 'function' ? console[level] : console.log
  const traceId = resolveResponseTraceId(response)

  logger(
    formatLogLine('[API 响应]', meta, {
      status: response && response.status,
      traceId,
      code: res && res.code,
      message: res && res.message,
      duration: getDuration(meta.startedAt),
      result: sanitizePayload(res)
    })
  )
}

export function logApiError(error) {
  const response = error && error.response
  const res = response && response.data
  const meta = getApiLogMeta((error && error.config) || (response && response.config))
  const traceId = resolveResponseTraceId(response)

  console.error(
    formatLogLine('[API 异常]', meta, {
      status: response && response.status,
      traceId,
      code: res && res.code,
      message: (res && res.message) || (error && error.message),
      duration: getDuration(meta.startedAt),
      result: sanitizePayload(res || { message: error && error.message })
    })
  )
}
